import { isViewMode } from "../contracts";
import type { ViewMode } from "../contracts";
import type { ActionFrame, DrillFrame, ListDrillFrame, NavigationStack, RecordFrame } from "./types";

export const WORKSPACE_BASE = "/wc";
const DEFAULT_VIEW_MODE: ViewMode = "list";

// All viewMode keywords that may appear as the second segment of any frame.
// Used to disambiguate the next frame's `key` from the current frame's
// optional `recordRef` when parsing the URL.
const ALL_VIEW_MODES = new Set<string>(["list", "kanban", "calendar", "form"]);

// Parse the splat segments after `/wc/{orgCode}/` into a typed navigation stack.
//
// Unified URL grammar — every frame is a triple `<key>/<viewMode>/[recordRef]`:
//   {actionPath}/{viewMode}[/{recordRef}]    ← root action frame
//   (
//     /{actionSlug}/{listViewMode}[/{recordRef}]    ← list drill (Enh 06)
//     | /{modelKey}/form/{recordRef}                ← record drill (action-less M2O, Mode A)
//   )*
//
// Disambiguation: after reading a frame's `key/viewMode`, the next segment is
// either THIS frame's recordRef OR the NEXT frame's key. We peek the segment
// AFTER that — if it's a known viewMode keyword (list|kanban|calendar|form),
// the segment between is the next frame's key (no recordRef on this frame).
// Otherwise it's this frame's recordRef.
export function parseNavigationStack(segments: string[]): NavigationStack {
    const segs = segments.filter((s) => s.length > 0);
    const actionKey = segs[0] ?? "";
    // A client action's URL is just the action key — NO second segment. When a
    // second segment IS present it's the view mode (an unknown keyword still
    // falls back to "list", preserving the window-action contract). Only the
    // genuinely-absent case yields `undefined` (a view-less client action).
    const hasViewSegment = segs.length > 1;
    const viewMode: ViewMode | undefined = !hasViewSegment
        ? undefined
        : (isViewMode(segs[1]) ? (segs[1] as ViewMode) : DEFAULT_VIEW_MODE);

    const action: ActionFrame = { kind: "action", actionKey, viewMode };
    const drills: DrillFrame[] = [];

    let i = 2;
    let currentSlot: { type: "action" | "list" | "record"; ref?: (rec: string) => void } = {
        type: "action",
        ref: (rec) => { action.recordRef = rec; },
    };

    while (i < segs.length) {
        // Decide whether `segs[i]` is the current frame's recordRef or the
        // start of a new drill frame. It's a new frame when the segment
        // TWO after it is a known viewMode keyword (i.e. segs[i+1] is the
        // new frame's viewMode and segs[i] is its key).
        const isNewFrame = i + 1 < segs.length && ALL_VIEW_MODES.has(segs[i + 1]);
        if (isNewFrame) {
            const key = segs[i];
            const vm = segs[i + 1];
            i += 2;
            if (vm === "form") {
                // Record drill — `modelKey/form/recordRef`. recordRef is
                // mandatory; if absent the frame is degenerate, skip it.
                const recordRef = segs[i];
                if (!recordRef) break;
                drills.push({ kind: "record", modelKey: key, recordRef, viewMode: "form" });
                i += 1;
                currentSlot = { type: "record" };
            } else {
                // List drill (list / kanban / calendar). recordRef optional;
                // captured by the next loop iteration if not a new frame.
                const frame: ListDrillFrame = {
                    kind: "list",
                    actionSlug: key,
                    viewMode: vm as ListDrillFrame["viewMode"],
                };
                drills.push(frame);
                currentSlot = {
                    type: "list",
                    ref: (rec) => { frame.recordRef = rec; },
                };
            }
        } else {
            // Trailing recordRef for the current frame.
            if (currentSlot.ref) currentSlot.ref(segs[i]);
            i += 1;
            currentSlot = { type: currentSlot.type };  // recordRef consumed
        }
    }

    return [action, ...drills];
}

// Inverse of parseNavigationStack — canonical URL for a stack under an org.
export function buildWorkspacePath(orgCode: string, stack: NavigationStack): string {
    const [action, ...drills] = stack;
    // Omit the view-mode segment entirely for view-less (client) actions, so
    // their canonical URL is `/wc/<org>/<actionKey>` with no trailing `/list`.
    const segs: string[] = [orgCode, action.actionKey];
    if (action.viewMode) segs.push(action.viewMode);
    if (action.recordRef) segs.push(action.recordRef);
    for (const frame of drills) {
        if (frame.kind === "list") {
            // `actionSlug/viewMode[/recordRef]` — same shape as the root.
            segs.push(frame.actionSlug, frame.viewMode);
            if (frame.recordRef) segs.push(frame.recordRef);
        } else {
            // `modelKey/form/recordRef` — the `/form/` segment makes the
            // grammar repeat uniformly (each frame is key/viewMode/[ref]).
            segs.push(frame.modelKey, frame.viewMode, frame.recordRef);
        }
    }
    return `${WORKSPACE_BASE}/${segs.filter((s) => s.length > 0).join("/")}`;
}

// ── Pure stack operations (each returns a new stack) ─────────────────────────

// Drill into a related record (m2o "open"). Pushes a RecordFrame.
export function pushRecord(stack: NavigationStack, frame: RecordFrame): NavigationStack {
    return [stack[0], ...stack.slice(1), frame] as NavigationStack;
}

// Drill into a filtered list (Related ▾ click, Enhancement 06). Pushes a
// ListDrillFrame; the upstream-most RecordFrame (or the action's recordRef)
// supplies `active_id` for the action's domain template at render-time.
export function pushList(stack: NavigationStack, frame: ListDrillFrame): NavigationStack {
    return [stack[0], ...stack.slice(1), frame] as NavigationStack;
}

// Back one frame. Never pops below the root action.
export function popFrame(stack: NavigationStack): NavigationStack {
    if (stack.length <= 1) return stack;
    return stack.slice(0, -1) as NavigationStack;
}

// Jump back to a given frame index (breadcrumb click).
export function popTo(stack: NavigationStack, index: number): NavigationStack {
    if (index <= 0) return [stack[0]];
    if (index >= stack.length) return stack;
    return stack.slice(0, index + 1) as NavigationStack;
}

// Switch the root view mode (list↔kanban↔calendar) — returns to the multi view,
// dropping any open record and the whole drill chain.
export function setViewMode(stack: NavigationStack, viewMode: ViewMode): NavigationStack {
    return [{ ...stack[0], viewMode, recordRef: undefined }];
}

// Open (or close, with undefined) a record within the root action; resets the drill chain.
export function setActionRecord(stack: NavigationStack, recordRef: string | undefined): NavigationStack {
    return [{ ...stack[0], recordRef }];
}

// ── Drill-frame predicates / accessors ──────────────────────────────────────

// Walk the drill chain backwards from the given index to find the closest
// upstream record context — either an explicit RecordFrame OR a list-drill
// frame that carries its own recordRef. Falls back to the root action's
// recordRef when no inner record exists. This is the `active_id` source
// for any downstream ListDrillFrame's domain substitution.
export function activeIdAbove(stack: NavigationStack, index: number): string | undefined {
    for (let i = index - 1; i >= 1; i--) {
        const f = stack[i];
        if (f.kind === "record") return f.recordRef;
        if (f.kind === "list" && f.recordRef) return f.recordRef;
    }
    return stack[0].recordRef;
}
